import {
  Button,
  Box,
  ErrorNotification,
  Label,
  upToMd,
  // get,
  // DatePicker,
} from '@ally/metronome-ui'
import React, { Dispatch, SetStateAction, useEffect } from 'react'
import {
  Forms,
  Form,
  useFormsContext,
  Select,
  SingleDatePicker,
} from '@ally/forms'
import { FlexDirectionProps } from 'styled-system'
// import dayjs from 'dayjs'
// import styled from 'styled-components'
import moment, { Moment } from 'moment'

interface FormProps {
  fileFormat: string
  dateRange: string
  startDate: string
  toDate: string
}

// NOTE - Istanbul ignore has been used in places that are confirmed covered by tests,
// but not getting proper coverage credit from istanbul
// TODO- add test coverage and remove ignores
const DownloadFormInner: React.FC<{
  shouldResetForm: boolean
  setShouldResetForm: Dispatch<SetStateAction<boolean>>
}> = ({ shouldResetForm, setShouldResetForm }) => {
  const {
    handleSubmit,
    values,
    formError,
    formErrorRef,
    formId,
    setFormError,
    setFieldValue,
    resetForm,
  } = useFormsContext<FormProps>()

  // This useEffect is to see if the form needs to be reset on account change or exit click
  useEffect(() => {
    // TODO - test this
    /* istanbul ignore next */
    if (shouldResetForm) {
      resetForm()
      setShouldResetForm(false)
    }
  }, [shouldResetForm])

  /* istanbul ignore next */
  const handleFileSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const fileFormat = e.target.value
    setFieldValue('fileFormat', fileFormat)
  }

  // TO DO: LEAVING THIS LOGIC TO USE AFTER METRONOME FIXES NEW DATE PICKER

  // const handleSelectChange = (
  //   e: React.ChangeEvent<HTMLSelectElement>,
  // ): void => {
  //   const today = dayjs().toString()
  //   let priorDate
  //   /* istanbul ignore next */
  //   if (e.target.value === 'all') {
  //     /* istanbul ignore next */
  //     priorDate = dayjs().subtract(18, 'months').format('YYYY-MM-DD').toString()
  //   } else if (e.target.value === 'custom') {
  //     priorDate = undefined
  //   } else {
  //     priorDate = dayjs()
  //       .subtract(Number(e.target.value), 'days')
  //       .format('YYYY-MM-DD')
  //       .toString()
  //   }
  //   setFieldValue('startDate', priorDate)
  //   setFieldValue('toDate', today)
  //   setFieldValue('dateRange', e.target.value)
  // }

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const today = moment().toString()
    let priorDate
    /* istanbul ignore next */
    if (e.target.value === 'all') {
      /* istanbul ignore next */
      priorDate = moment().subtract(18, 'months').toString()
    } else {
      priorDate = moment().subtract(e.target.value, 'days').toString()
    }
    setFieldValue('startDate', priorDate)
    setFieldValue('toDate', today)
    setFieldValue('dateRange', e.target.value)
  }

  const validateFileSelect = (option: string): string => {
    /* istanbul ignore else */
    if (!option) {
      // formError means > 1 error, which isn't possible without a selection
      // so manually clear formError here.
      setFormError('')
      return 'Select a file format.'
    }
    return ''
  }

  const validateSelect = (option: string): string => {
    if (!option) {
      // formError means > 1 error, which isn't possible without a selection
      // so manually clear formError here.
      setFormError('')
      return 'Select or enter a date range.'
    }
    return ''
  }

  const isInTheFuture = (date: Moment | string): boolean => {
    return moment(date).startOf('day').isAfter()
  }

  const isValidDate = (date: string): boolean => {
    return moment(date, 'M/D/YYYY', true).isValid()
  }

  const isBefore18Months = (date: string | Moment): boolean => {
    const earliestFromDate = moment().subtract(18, 'months').startOf('day')
    const dateAsMoment = moment(date, 'M/D/YYYY', true).startOf('day')
    return earliestFromDate > dateAsMoment
  }

  const validateStartDate = (startDate: string): string => {
    /* istanbul ignore else */
    if (values.dateRange) {
      if (!isValidDate(startDate)) {
        return 'Enter a valid start date.'
      }
      if (isInTheFuture(startDate)) {
        return 'Enter a FROM date that is in the past.'
      }
      /* istanbul ignore else */
      if (isBefore18Months(startDate)) {
        return 'Narrow your date range to the last 18 months.'
      }
    }
    /* istanbul ignore next */
    return ''
  }
  const validateToDate = (toDate: string): string => {
    /* istanbul ignore else */
    if (values.dateRange) {
      /* istanbul ignore else */
      if (!isValidDate(toDate)) {
        return 'Enter a valid end date.'
      }
      if (isInTheFuture(toDate)) {
        return 'Enter a TO date that is in the past.'
      }
      /* istanbul ignore else */
      if (values.startDate && moment(values.startDate).isAfter(toDate)) {
        return 'Enter a TO date that is after the FROM date.'
      }
    }
    /* istanbul ignore next */
    return ''
  }

  const invalidStartDates = React.useCallback(
    (date: Moment): boolean => {
      /* istanbul ignore next */
      return (
        isInTheFuture(date) ||
        date.isAfter(values.toDate, 'day') ||
        isBefore18Months(date)
      )
    },
    [values.toDate],
  )

  const invalidToDates = React.useCallback(
    (date: Moment): boolean => {
      /* istanbul ignore next */
      return isInTheFuture(date) || date.isBefore(values.startDate, 'day')
    },
    [values.startDate],
  )

  /* istanbul ignore next */
  const formValueMonthOrThisMonth = React.useCallback(
    (field: keyof FormProps) => {
      if (isValidDate(values[field])) {
        return moment(values[field])
      }
      return moment()
    },
    [values],
  )

  const fileOptions = [
    {
      label: 'CSV',
      value: 'csv',
    },
    {
      label: 'Quicken',
      value: 'qfx',
    },
  ]

  const options = [
    {
      label: '30 days',
      value: '30',
    },
    {
      label: '60 days',
      value: '60',
    },
    {
      label: '90 days',
      value: '90',
    },
    {
      label: '120 days',
      value: '120',
    },
    {
      label: 'Custom',
      value: 'custom',
    },
    {
      label: 'All available dates',
      value: 'all',
    },
  ]

  // TO DO: LEAVING THIS LOGIC TO USE AFTER METRONOME FIXES NEW DATE PICKER

  // const onDateChangeFrom = (date: any): void => {
  //   setFieldValue('startDate', date.format('YYYY-MM-DD').toString())
  // }
  // const onDateChangeTo = (date: any): void => {
  //   setFieldValue('toDate', date.format('YYYY-MM-DD').toString())
  // }

  // const initialSelectedDateFrom = values.startDate
  //   ? dayjs(values.startDate)
  //   : undefined

  // const initialSelectedDateTo = values.startDate
  //   ? dayjs(values.toDate)
  //   : undefined
  // const minDateFrom = dayjs().subtract(18, 'months')
  return (
    <Form id={formId}>
      <Box mb="xs">
        <ErrorNotification
          errorMessage={formError}
          ref={formErrorRef}
          tabIndex={-1}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={
          upToMd('column', 'row') as FlexDirectionProps['flexDirection']
        }
        width={upToMd(12, 6)}
      >
        <Box mb="sm" width={upToMd('100%', '50%')} pr={upToMd('none', 'xs')}>
          <Label display="block" htmlFor="fileFormat">
            Select File Format
          </Label>
          <Select
            name="fileFormat"
            options={fileOptions}
            placeholder="Select"
            validate={validateFileSelect}
            onChange={handleFileSelectChange}
            data-testid="file-format"
          />
        </Box>
        <Box mb="sm" width={upToMd('100%', '50%')} pl={upToMd('none', 'xs')}>
          <Label display="block" htmlFor="dateRange">
            Date Range
          </Label>
          <Select
            name="dateRange"
            options={options}
            placeholder="Select"
            validate={validateSelect}
            onChange={handleSelectChange}
            data-testid="date-picker"
          />
        </Box>
      </Box>

      {values.dateRange === 'custom' && (
        <Box
          display="flex"
          flexDirection={
            upToMd('column', 'row') as FlexDirectionProps['flexDirection']
          }
          width={upToMd(12, 6)}
        >
          <Box mb="sm" width={upToMd('100%', '50%')} pr={upToMd('none', 'xs')}>
            {/* <DatePicker
              label="From"
              onDateSelected={onDateChangeFrom}
              disableFutureDates
              minDate={minDateFrom}
              initialSelectedDate={initialSelectedDateFrom}
            /> */}
            <Label htmlFor="startDate">From</Label>
            <SingleDatePicker
              name="startDate"
              ariaLabel="Start Date"
              disabled={false}
              initialVisibleMonth={
                /* istanbul ignore next */
                (): Moment => formValueMonthOrThisMonth('startDate')
              }
              keepOpenOnDateSelect={false}
              isOutsideRange={invalidStartDates}
              validate={validateStartDate}
            />
          </Box>

          <Box mb="sm" width={upToMd('100%', '50%')} pl={upToMd('none', 'xs')}>
            {/* <DatePicker
              label="To"
              onDateSelected={onDateChangeTo}
              disableFutureDates
              initialSelectedDate={initialSelectedDateTo}
              minDate={values.startDate ? dayjs(values.startDate) : undefined}
            /> */}
            <Label htmlFor="toDate">To</Label>
            <SingleDatePicker
              name="toDate"
              ariaLabel="End Date"
              disabled={false}
              initialVisibleMonth={
                /* istanbul ignore next */
                (): Moment => formValueMonthOrThisMonth('toDate')
              }
              keepOpenOnDateSelect={false}
              isOutsideRange={invalidToDates}
              validate={validateToDate}
            />
          </Box>
        </Box>
      )}

      <Button
        onClick={(): void => {
          handleSubmit()
        }}
        text="Download"
        withAllyTM="button-download-transaction-history"
        my="sm"
        data-testid="download-submit-button"
      />
    </Form>
  )
}

export const DownloadForm: React.FC<{
  handleSubmit: any
  shouldResetForm: boolean
  setShouldResetForm: Dispatch<SetStateAction<boolean>>
}> = ({ handleSubmit, shouldResetForm, setShouldResetForm }) => {
  const initialValues: FormProps = {
    fileFormat: '',
    dateRange: '',
    startDate: '',
    toDate: '',
  }

  return (
    <Forms
      formId="transactionHistoryDownload"
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <DownloadFormInner
        shouldResetForm={shouldResetForm}
        setShouldResetForm={setShouldResetForm}
      />
    </Forms>
  )
}
